import { CREDIT_RATES } from '@lyrmo/shared';
import type { I18n } from './i18n-context';
import type { Locale } from './locales';

// Activity labels for the shared credit balance, independent of plan and provider.
const LABELS: Readonly<Record<Locale, readonly [string, string, string]>> = {
  en: ['Text', 'Audio to text', 'Speech'],
  es: ['Texto', 'Audio a texto', 'Voz'],
  fr: ['Texte', 'Audio en texte', 'Voix'],
  de: ['Text', 'Audio zu Text', 'Sprachausgabe'],
  it: ['Testo', 'Audio in testo', 'Voce'],
  pt: ['Texto', 'Áudio para texto', 'Voz'],
  nl: ['Tekst', 'Audio naar tekst', 'Spraak'],
  pl: ['Tekst', 'Audio na tekst', 'Mowa'],
  uk: ['Текст', 'Аудіо в текст', 'Озвучення'],
  cs: ['Text', 'Zvuk na text', 'Řeč'],
  sv: ['Text', 'Ljud till text', 'Tal'],
  da: ['Tekst', 'Lyd til tekst', 'Tale'],
  no: ['Tekst', 'Lyd til tekst', 'Tale'],
  fi: ['Teksti', 'Äänestä tekstiksi', 'Puhe'],
  ro: ['Text', 'Audio în text', 'Vorbire'],
  el: ['Κείμενο', 'Ήχος σε κείμενο', 'Ομιλία'],
  tr: ['Metin', 'Sesten metne', 'Konuşma'],
  ar: ['نص', 'صوت إلى نص', 'صوت'],
  he: ['טקסט', 'מאודיו לטקסט', 'דיבור'],
  hi: ['टेक्स्ट', 'ऑडियो से टेक्स्ट', 'आवाज़'],
  id: ['Teks', 'Audio ke teks', 'Suara'],
  vi: ['Văn bản', 'Âm thanh thành văn bản', 'Giọng nói'],
  th: ['ข้อความ', 'เสียงเป็นข้อความ', 'เสียงพูด'],
  ja: ['テキスト', '音声をテキストに', '読み上げ'],
  ko: ['텍스트', '음성을 텍스트로', '음성'],
  zh: ['文本', '音频转文字', '语音'],
};

// Caption, column headings, and the units counted for each activity.
const TABLE_COPY: Readonly<
  Record<Locale, readonly [string, string, string, string, string, string, string]>
> = {
  en: [
    'How credits are used',
    'Activity',
    'Credits',
    'Charged for',
    'Each word submitted',
    'Each second of audio',
    'Each word spoken',
  ],
  es: [
    'Cómo se usan los créditos',
    'Actividad',
    'Créditos',
    'Se cobra por',
    'Cada palabra enviada',
    'Cada segundo de audio',
    'Cada palabra pronunciada',
  ],
  fr: [
    'Comment les crédits sont utilisés',
    'Activité',
    'Crédits',
    'Facturé pour',
    'Chaque mot envoyé',
    'Chaque seconde audio',
    'Chaque mot prononcé',
  ],
  de: [
    'So werden Credits verwendet',
    'Aktivität',
    'Credits',
    'Berechnet für',
    'Jedes eingereichte Wort',
    'Jede Sekunde Audio',
    'Jedes gesprochene Wort',
  ],
  it: [
    'Come vengono usati i crediti',
    'Attività',
    'Crediti',
    'Addebito per',
    'Ogni parola inviata',
    'Ogni secondo di audio',
    'Ogni parola pronunciata',
  ],
  pt: [
    'Como os créditos são usados',
    'Atividade',
    'Créditos',
    'Cobrado por',
    'Cada palavra enviada',
    'Cada segundo de áudio',
    'Cada palavra falada',
  ],
  nl: [
    'Hoe credits worden gebruikt',
    'Activiteit',
    'Credits',
    'Kosten voor',
    'Elk ingestuurd woord',
    'Elke seconde audio',
    'Elk uitgesproken woord',
  ],
  pl: [
    'Jak są używane kredyty',
    'Aktywność',
    'Kredyty',
    'Opłata za',
    'Każde przesłane słowo',
    'Każda sekunda nagrania',
    'Każde wypowiedziane słowo',
  ],
  uk: [
    'Як використовуються кредити',
    'Дія',
    'Кредити',
    'За що списуються',
    'Кожне надіслане слово',
    'Кожна секунда аудіо',
    'Кожне озвучене слово',
  ],
  cs: [
    'Jak se používají kredity',
    'Činnost',
    'Kredity',
    'Účtováno za',
    'Každé odeslané slovo',
    'Každá sekunda zvuku',
    'Každé vyslovené slovo',
  ],
  sv: [
    'Så används krediter',
    'Aktivitet',
    'Krediter',
    'Debiteras för',
    'Varje inskickat ord',
    'Varje sekund ljud',
    'Varje uppläst ord',
  ],
  da: [
    'Sådan bruges kreditter',
    'Aktivitet',
    'Kreditter',
    'Betaling for',
    'Hvert indsendt ord',
    'Hvert sekund lyd',
    'Hvert oplæst ord',
  ],
  no: [
    'Slik brukes kreditter',
    'Aktivitet',
    'Kreditter',
    'Belastes for',
    'Hvert innsendte ord',
    'Hvert sekund lyd',
    'Hvert oppleste ord',
  ],
  fi: [
    'Näin krediittejä käytetään',
    'Toiminto',
    'Krediitit',
    'Veloitusperuste',
    'Jokainen lähetetty sana',
    'Jokainen äänisekunti',
    'Jokainen ääneen luettu sana',
  ],
  ro: [
    'Cum sunt folosite creditele',
    'Activitate',
    'Credite',
    'Se taxează pentru',
    'Fiecare cuvânt trimis',
    'Fiecare secundă audio',
    'Fiecare cuvânt rostit',
  ],
  el: [
    'Πώς χρησιμοποιούνται οι πιστώσεις',
    'Δραστηριότητα',
    'Πιστώσεις',
    'Χρέωση για',
    'Κάθε λέξη που υποβάλλεται',
    'Κάθε δευτερόλεπτο ήχου',
    'Κάθε λέξη που εκφωνείται',
  ],
  tr: [
    'Krediler nasıl kullanılır',
    'İşlem',
    'Kredi',
    'Ücretlendirme',
    'Gönderilen her kelime',
    'Sesin her saniyesi',
    'Seslendirilen her kelime',
  ],
  ar: [
    'كيف يُستخدم الرصيد',
    'النشاط',
    'الرصيد',
    'يُحسب لكل',
    'كل كلمة مرسلة',
    'كل ثانية من الصوت',
    'كل كلمة منطوقة',
  ],
  he: [
    'איך משתמשים בקרדיטים',
    'פעילות',
    'קרדיטים',
    'החיוב עבור',
    'כל מילה שנשלחת',
    'כל שנייה של אודיו',
    'כל מילה שמוקראת',
  ],
  hi: [
    'क्रेडिट कैसे इस्तेमाल होते हैं',
    'गतिविधि',
    'क्रेडिट',
    'शुल्क किसके लिए',
    'भेजा गया हर शब्द',
    'ऑडियो का हर सेकंड',
    'बोला गया हर शब्द',
  ],
  id: [
    'Cara kredit digunakan',
    'Aktivitas',
    'Kredit',
    'Dikenakan untuk',
    'Setiap kata yang dikirim',
    'Setiap detik audio',
    'Setiap kata yang diucapkan',
  ],
  vi: [
    'Cách sử dụng tín dụng',
    'Hoạt động',
    'Tín dụng',
    'Tính phí cho',
    'Mỗi từ được gửi',
    'Mỗi giây âm thanh',
    'Mỗi từ được đọc',
  ],
  th: [
    'วิธีใช้เครดิต',
    'กิจกรรม',
    'เครดิต',
    'คิดค่าบริการสำหรับ',
    'ทุกคำที่ส่ง',
    'ทุกวินาทีของเสียง',
    'ทุกคำที่อ่านออกเสียง',
  ],
  ja: [
    'クレジットの使い方',
    '操作',
    'クレジット',
    '課金対象',
    '送信した単語ごと',
    '音声1秒ごと',
    '読み上げた単語ごと',
  ],
  ko: [
    '크레딧 사용 방식',
    '활동',
    '크레딧',
    '과금 기준',
    '제출한 단어마다',
    '오디오 1초마다',
    '읽어 준 단어마다',
  ],
  zh: ['积分如何使用', '活动', '积分', '计费依据', '每个提交的词', '每秒音频', '每个朗读的词'],
};

// AI tools' label, and the words they're charged for.
const TOOL_COPY: Readonly<Record<Locale, readonly [string, string]>> = {
  en: ['AI tools', 'Each word formatted'],
  es: ['Herramientas de IA', 'Cada palabra formateada'],
  fr: ['Outils IA', 'Chaque mot mis en forme'],
  de: ['KI-Werkzeuge', 'Jedes formatierte Wort'],
  it: ['Strumenti IA', 'Ogni parola formattata'],
  pt: ['Ferramentas de IA', 'Cada palavra formatada'],
  nl: ['AI-tools', 'Elk opgemaakt woord'],
  pl: ['Narzędzia AI', 'Każde sformatowane słowo'],
  uk: ['ШІ-інструменти', 'Кожне відформатоване слово'],
  cs: ['Nástroje AI', 'Každé formátované slovo'],
  sv: ['AI-verktyg', 'Varje formaterat ord'],
  da: ['AI-værktøjer', 'Hvert formateret ord'],
  no: ['AI-verktøy', 'Hvert formaterte ord'],
  fi: ['Tekoälytyökalut', 'Jokainen muotoiltu sana'],
  ro: ['Instrumente AI', 'Fiecare cuvânt formatat'],
  el: ['Εργαλεία AI', 'Κάθε λέξη που μορφοποιείται'],
  tr: ['Yapay zekâ araçları', 'Biçimlendirilen her kelime'],
  ar: ['أدوات الذكاء الاصطناعي', 'كل كلمة يتم تنسيقها'],
  he: ['כלי AI', 'כל מילה שמעוצבת'],
  hi: ['AI टूल', 'फ़ॉर्मैट किया गया हर शब्द'],
  id: ['Alat AI', 'Setiap kata yang diformat'],
  vi: ['Công cụ AI', 'Mỗi từ được định dạng'],
  th: ['เครื่องมือ AI', 'ทุกคำที่จัดรูปแบบ'],
  ja: ['AIツール', '整形した単語ごと'],
  ko: ['AI 도구', '서식을 적용한 단어마다'],
  zh: ['AI 工具', '每个被排版的词'],
};

// Images' label, and what they're charged for: each one read or described.
const IMAGE_TEXT_COPY: Readonly<Record<Locale, readonly [string, string]>> = {
  en: ['Images', 'Each image read or described'],
  es: ['Imágenes', 'Cada imagen leída o descrita'],
  fr: ['Images', 'Chaque image lue ou décrite'],
  de: ['Bilder', 'Jedes gelesene oder beschriebene Bild'],
  it: ['Immagini', 'Ogni immagine letta o descritta'],
  pt: ['Imagens', 'Cada imagem lida ou descrita'],
  nl: ['Afbeeldingen', 'Elke gelezen of beschreven afbeelding'],
  pl: ['Obrazy', 'Każdy odczytany lub opisany obraz'],
  uk: ['Зображення', 'Кожне розпізнане чи описане зображення'],
  cs: ['Obrázky', 'Každý přečtený nebo popsaný obrázek'],
  sv: ['Bilder', 'Varje bild som läses eller beskrivs'],
  da: ['Billeder', 'Hvert billede, der læses eller beskrives'],
  no: ['Bilder', 'Hvert bilde som leses eller beskrives'],
  fi: ['Kuvat', 'Jokainen luettu tai kuvailtu kuva'],
  ro: ['Imagini', 'Fiecare imagine citită sau descrisă'],
  el: ['Εικόνες', 'Κάθε εικόνα που διαβάζεται ή περιγράφεται'],
  tr: ['Görseller', 'Okunan veya betimlenen her görsel'],
  ar: ['الصور', 'كل صورة تُقرأ أو تُوصف'],
  he: ['תמונות', 'כל תמונה שנקראת או מתוארת'],
  hi: ['चित्र', 'पढ़ा गया या वर्णित हर चित्र'],
  id: ['Gambar', 'Setiap gambar yang dibaca atau dideskripsikan'],
  vi: ['Ảnh', 'Mỗi ảnh được đọc hoặc mô tả'],
  th: ['รูปภาพ', 'ทุกรูปที่อ่านหรือบรรยาย'],
  ja: ['画像', '読み取りまたは説明した画像1枚ごと'],
  ko: ['이미지', '읽거나 설명한 이미지마다'],
  zh: ['图片', '每张识别或描述的图片'],
};

export function creditRateTable({ locale, t, format }: I18n) {
  const [text, dictation, speech] = LABELS[locale];
  const [caption, activity, credits, chargedFor, inputWord, audioSecond, spokenWord] =
    TABLE_COPY[locale];
  const [tools, formattedWord] = TOOL_COPY[locale];
  const [imageText, extractedWord] = IMAGE_TEXT_COPY[locale];
  const amount = (n: number) => `${format.number(n)}${t.account.chipUnit(n)}`;
  return {
    caption,
    headings: [activity, credits, chargedFor],
    rows: [
      {
        id: 'text' as const,
        label: text,
        credits: amount(CREDIT_RATES.rewritePerWord),
        basis: inputWord,
      },
      {
        id: 'tools' as const,
        label: tools,
        credits: amount(CREDIT_RATES.toolPerWord),
        basis: formattedWord,
      },
      {
        id: 'dictation' as const,
        label: dictation,
        credits: amount(CREDIT_RATES.transcriptionPerSecond),
        basis: audioSecond,
      },
      {
        id: 'speech' as const,
        label: speech,
        credits: amount(CREDIT_RATES.speechPerWord),
        basis: spokenWord,
      },
      {
        id: 'imageText' as const,
        label: imageText,
        credits: amount(CREDIT_RATES.imagePerImage),
        basis: extractedWord,
      },
    ],
  };
}
